// สั่งเครื่องโหลดหน้าใหม่จาก Firestore — อ่านค่าจาก appControl store (source of truth)
// แล้วตัดสินว่า "ต้องโหลดใหม่ไหม" เก็บไว้ใน pendingReload ให้ SharedAppReload โหลดเมื่อเครื่องว่าง
//
//   app_control/{channel}.reload          → ทุกร้านบน channel นั้น
//   app_control_stores/{storeId}.reload   → ทุกเครื่องของร้านนั้น
//
// ทั้งสองใช้กติกาเดียวกัน: hash "ต่าง" จาก hash ตัวแรกที่ tab นี้เห็น = สั่งโหลด (ไม่เทียบเลขเวอร์ชัน)
// จำ hash ไว้ในหน่วยความจำของ tab เท่านั้น ไม่ใช้ localStorage — ดูทั้ง flow ที่ plans/app-reload.md
//   - เปิดเบราว์เซอร์หลังสั่ง reload = hash ตัวแรกคือค่าใหม่อยู่แล้ว ไม่โหลดซ้ำ (bundle ใหม่อยู่แล้ว)
//   - tab อื่นในเบราว์เซอร์เดียวกันเห็น hash ก่อน ไม่ทำให้ tab นี้พลาดคำสั่ง (localStorage ใช้ร่วมกันทุก tab)
//   - โหลดใหม่แล้วไม่วน เพราะหน้าใหม่จับ hash ปัจจุบันเป็นค่าตั้งต้น
//
// นอกจากคำสั่งจาก Firestore แล้ว เครื่องตัดสินเองได้ผ่าน requestReload() (ไม่มีคนสั่ง):
//   session_stale                  → มีรอบการขายใหม่กว่าที่เครื่องถือ (stores/jobs.ts)
//   stale_tab                      → tab เปิดค้างเกิน MAX_PAGE_AGE_MS (ไฟล์นี้)
//   deploy                         → มี build ใหม่ (plugins/deployCheck.client.ts — ยังปิดไว้)
// ทั้งหมดรอเครื่องว่างเหมือนกัน (components/shared/AppReload.vue)
//
// เป็น pinia plugin ของสโตร์ appControl: state/action ของ reload ถูกเติมเข้าไปในสโตร์
// (store.pendingReload, store.reloadNow() ...) แต่โค้ดทั้งหมดอยู่ไฟล์นี้ไฟล์เดียว
// เพิ่ม/ถอดฟีเจอร์ reload แก้ที่นี่ ไม่ต้องแตะ appControl.ts

import * as Sentry from "@sentry/vue";
import { ref, watch } from "vue";
import type { PiniaPluginContext } from "pinia";
import type { AppControlReload, BuildChannel } from "~/interface/AppControl";
import { PAGE_LOADED_AT_MS, formatDuration } from "../utils/presenceInfo";

/**
 * ธงฝากข้ามการโหลดหน้า ว่ารอบนี้เพิ่งโหลดใหม่เพราะคำสั่ง reload
 * ชื่อ prefix คงไว้ตามเดิม เปลี่ยนแล้วเครื่องที่เขียนธงไว้ก่อนอัปเดตจะไม่เห็น toast
 */
export const JUST_UPDATED_KEY_PREFIX = "forceReloadJustUpdated_";

/**
 * สุ่มเวลาโหลดภายใน 0-60 วิหลังได้คำสั่ง — ทุกเครื่องได้ snapshot พร้อมกัน
 * ถ้าโหลดพร้อมกันหมด hosting / API / Firebase โดน request ก้อนเดียวในวินาทีเดียว
 */
const RELOAD_JITTER_MS = 60_000;

/**
 * ธงเพิ่งอัปเดตมีอายุสั้น ๆ เผื่อกรณีเขียนธงแล้วการโหลดไม่จบ (ผู้ใช้ปิดเครื่อง
 * ระหว่างนั้นพอดี) จะได้ไม่มี toast โผล่ข้ามวันมาโดยไม่มีอะไรเกิดขึ้นจริง
 */
const JUST_UPDATED_TTL_MS = 5 * 60 * 1000;

/** ค่าที่จำแทน "ไม่มี reload.hash" — ห้ามชนกับ hash จริง (สคริปต์สุ่มเป็น hex) */
const NO_RELOAD_HASH = "-";

export const justUpdatedKey = (channel: BuildChannel) => `${JUST_UPDATED_KEY_PREFIX}${channel}`;

/**
 * tab เปิดค้างนานเกินนี้ = โหลดใหม่เมื่อว่าง กันพลาดเผื่อสัญญาณอื่นไม่มา
 * (เช่น tab บนมือถือที่ทิ้งไว้เป็นสัปดาห์ เบราว์เซอร์ปลุกขึ้นมาแป๊บ ๆ แล้วสะสม job ไปเรื่อย ๆ)
 */
const MAX_PAGE_AGE_MS = 24 * 60 * 60 * 1000;
/** timer ของ tab เบื้องหลังโดนหน่วง จึงเช็คซ้ำตอน tab กลับมาด้วย (visibilitychange / pageshow) */
const PAGE_AGE_CHECK_MS = 5 * 60 * 1000;

/**
 * reload ที่เครื่องตัดสินเองแล้ว "โหลดไปแล้ว" ใน tab นี้ — sessionStorage อยู่รอดข้ามการโหลดหน้า
 * กันโหลดวน: หลังโหลดแล้วสัญญาณเดิมยังอยู่ (เช่น jobchain ล้างไม่สำเร็จ) จะไม่โหลดซ้ำ
 */
const AUTO_RELOAD_DONE_KEY_PREFIX = "autoReloadDone_";

/** มาจาก Firestore (มีคนสั่ง) */
export type RemoteReloadSource = "app_control" | "store";
/** เครื่องตัดสินเอง — ไม่ขึ้น toast "ระบบมีการอัปเดต" หลังโหลด */
export type LocalReloadSource = "session_stale" | "stale_tab" | "deploy";
export type ReloadSource = RemoteReloadSource | LocalReloadSource;

export interface LocalReloadRequest {
  source: LocalReloadSource;
  /** ไม่ซ้ำต่อสัญญาณ — โหลดไปแล้วด้วย key นี้ใน tab นี้จะไม่โหลดอีก */
  key: string;
  reason: string;
  /** หลายเครื่องได้สัญญาณพร้อมกัน (ปิดร้าน) → สุ่มเวลาเหมือนคำสั่งจาก Firestore */
  jitter?: boolean;
}

export interface PendingReload {
  hash: string;
  source: ReloadSource;
  /** ส่งขึ้น Sentry */
  reason: string | null;
  /** บรรทัดที่สองของ toast หลังโหลดเสร็จ (ไม่มี = มีแค่ "ระบบมีการอัปเดตเรียบร้อยแล้ว") */
  message: string | null;
  /** epoch ms ที่สุ่มไว้ — โหลดได้ตั้งแต่เวลานี้ ถ้าเครื่องว่าง (ดู RELOAD_JITTER_MS) */
  reloadAt: number;
  /** epoch ms ที่ได้สัญญาณ — AppReload.vue ใช้จับเครื่องที่ติด busy นานเกินไป */
  createdAt: number;
}

declare module "pinia" {
  export interface PiniaCustomProperties {
    pendingReload: PendingReload | null;
    updatedToastUntil: number | null;
    updatedMessage: string | null;
    reloadNow: () => void;
    requestReload: (request: LocalReloadRequest) => void;
    consumeJustUpdated: () => boolean;
    startUpdatedToast: (durationMs: number) => void;
    hideUpdatedToast: () => void;
  }
}

function AppReloadPlugin({ store }: PiniaPluginContext) {
  if (store.$id !== "appControl") return;

  // คำสั่งที่รอให้ผู้ใช้ตอบ / รอเครื่องว่าง — null = ไม่มีอะไรต้องทำ
  const pendingReload = ref<PendingReload | null>(null);

  // ตั้งตอนบูตถ้าเจอธง "เพิ่งอัปเดต" — รอให้ layout หลักขึ้นแล้วค่อยโชว์ toast
  let justUpdated = false;
  // reload.message ของคำสั่งที่ทำให้โหลดรอบนี้ ฝากมากับธง โชว์เป็นบรรทัดที่สองของ toast
  const updatedMessage = ref<string | null>(null);
  // เก็บเป็น deadline ไม่ใช่ ref ในคอมโพเนนต์ เพราะ layout หลักถูก unmount/remount
  // ระหว่าง "/" → "/pos" ตอนบูต ถ้าเก็บในคอมโพเนนต์ toast จะหายไปด้วย
  const updatedToastUntil = ref<number | null>(null);

  // channel ถูกตั้งตอน store.start() — อ่านค่าที่จดไว้ของ channel นั้นครั้งเดียว
  let initedChannel: BuildChannel | null = null;
  function ensureInit() {
    const channel = store.channel as BuildChannel;
    if (initedChannel === channel) return;
    initedChannel = channel;
    const flag = consumeJustUpdatedFlag(channel);
    justUpdated = flag !== null;
    updatedMessage.value = flag?.message ?? null;
  }

  // hash ที่ tab นี้เห็นแล้ว (ตัวแรก = ค่าตั้งต้น) — null = ยังไม่ได้คำตอบจาก server
  let seenAppHash: string | null = null;
  // ของร้าน ผูกกับ storeId — สลับร้านแล้ว snapshot แรกของร้านใหม่เป็นค่าตั้งต้นใหม่
  let seenStoreHash: string | null = null;
  let seenStoreId = "";

  /**
   * ตัดสิน reload.hash ของ app_control/{channel} — เรียกซ้ำได้ทุก snapshot ต้อง idempotent
   * ใช้เฉพาะ snapshot จาก server (serverDoc) ค่าจากแคชอาจเก่ากว่า bundle ที่เพิ่งโหลดมา
   */
  function evaluateAppReload() {
    ensureInit();
    const doc = store.serverDoc;
    if (doc === undefined) return;

    const hash = normalizeHash(doc?.reload?.hash) ?? NO_RELOAD_HASH;
    if (seenAppHash === null) {
      seenAppHash = hash;
      return;
    }
    if (hash === seenAppHash) return;
    seenAppHash = hash;
    decide(hash, doc?.reload, "app_control");
  }

  /**
   * ตัดสิน reload.hash ของ app_control_stores/{storeId}
   * store เก็บเฉพาะ snapshot ที่ยืนยันจาก server แล้ว (ไม่ใช่แคช)
   */
  function evaluateStoreReload() {
    ensureInit();
    const storeId = store.storeId as string;
    // ยังไม่ได้คำตอบจาก server / snapshot ของร้านเก่าที่มาช้าหลังสลับร้าน
    if (!storeId || store.storeDocStoreId !== storeId) return;

    const reload = store.storeDoc?.reload as AppControlReload | undefined;
    const hash = normalizeHash(reload?.hash) ?? NO_RELOAD_HASH;
    if (seenStoreId !== storeId) {
      seenStoreId = storeId;
      seenStoreHash = hash;
      return;
    }
    if (hash === seenStoreHash) return;
    seenStoreHash = hash;
    decide(hash, reload, "store");
  }

  function decide(hash: string, reload: AppControlReload | undefined, source: ReloadSource) {
    // ฟิลด์ reload ถูกลบออก = ไม่ใช่คำสั่งโหลด แค่จำว่าตอนนี้ไม่มี
    if (hash === NO_RELOAD_HASH) return;

    const pending: PendingReload = {
      hash,
      source,
      reason: text(reload?.reason),
      message: text(reload?.message),
      // คำสั่งใหม่ระหว่างที่ตัวเก่ายังรออยู่ ใช้เวลาที่สุ่มไว้เดิม ไม่เลื่อนออกไปอีก
      reloadAt: pendingReload.value?.reloadAt ?? Date.now() + Math.floor(Math.random() * RELOAD_JITTER_MS),
      createdAt: pendingReload.value?.createdAt ?? Date.now(),
    };

    // คำสั่งใหม่ทับคำสั่งที่ค้างอยู่ (message ล่าสุดชนะ)
    pendingReload.value = pending;

    reportToSentry(store.channel as BuildChannel, pending, store.storeId as string);
  }

  watch(() => store.serverDoc, evaluateAppReload);
  watch(() => [store.storeId, store.storeDocStoreId, store.storeDoc], evaluateStoreReload);

  /**
   * เครื่องตัดสินเองว่าควรโหลดใหม่ — รอเครื่องว่างเหมือนคำสั่งจาก Firestore
   * มีคำสั่งรออยู่แล้วไม่ทับ (คำสั่งจาก Firestore มี message ให้ขึ้น toast)
   */
  function requestReload(request: LocalReloadRequest) {
    if (pendingReload.value) return;
    if (safeSessionGet(autoReloadDoneKey(request.key)) !== null) return;

    const now = Date.now();
    pendingReload.value = {
      hash: request.key,
      source: request.source,
      reason: request.reason,
      message: null,
      reloadAt: now + (request.jitter ? Math.floor(Math.random() * RELOAD_JITTER_MS) : 0),
      createdAt: now,
    };

    // เปิดรอบใหม่เกิดทุกวันทุกเครื่อง ส่งเป็น breadcrumb พอ ไม่ต้องเป็น event
    try {
      Sentry.addBreadcrumb({
        category: "app-reload",
        level: "info",
        message: `auto reload: ${request.source}`,
        data: { key: request.key, reason: request.reason },
      });
    } catch {}
  }

  // tab ค้างนานเกินไป — เช็คเป็นระยะ และทันทีที่ tab กลับมา (timer ของ tab เบื้องหลังโดนหน่วง)
  function checkPageAge() {
    const age = Date.now() - PAGE_LOADED_AT_MS;
    if (age < MAX_PAGE_AGE_MS) return;
    requestReload({
      source: "stale_tab",
      key: `stale_tab:${PAGE_LOADED_AT_MS}`,
      reason: `tab เปิดค้าง ${formatDuration(age)}`,
    });
  }
  setInterval(checkPageAge, PAGE_AGE_CHECK_MS);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") checkPageAge();
  });
  window.addEventListener("pageshow", checkPageAge);

  function reloadNow() {
    const pending = pendingReload.value;
    if (pending && isLocalSource(pending.source)) {
      // จดก่อนโหลดเสมอ ไม่งั้นสัญญาณเดิมที่ยังอยู่หลังโหลดจะสั่งโหลดวน
      safeSessionSet(autoReloadDoneKey(pending.hash), String(Date.now()));
    } else {
      // ฝากธงไว้ให้รอบหน้ารู้ว่าเพิ่งอัปเดตมา แล้วค่อยขึ้น toast (พร้อม reload.message ถ้ามี)
      //
      // ใช้ localStorage ไม่ใช่ query param เพราะ query รอดไม่ถึงปลายทางเสมอไป:
      // ถ้ารอบนี้ต้องล็อกอินใหม่ ทั้ง pages/login.vue และ middleware/auth.global.ts
      // จะพาไปต่อด้วย `window.location.href = "/"` แบบไม่มี query ติดไปด้วย
      const flag: JustUpdatedFlag = { at: Date.now(), message: pending?.message ?? null };
      safeSet(justUpdatedKey(store.channel as BuildChannel), JSON.stringify(flag));
    }

    // โหลดกลับไปที่ root เสมอ ไม่ใช่หน้าปัจจุบัน — app.vue จะ push("/") อยู่แล้ว
    // ถ้าโหลดทับหน้าเดิมจะเกิด redirect ไป-กลับ เสียเวลาบูตเปล่า ๆ
    location.replace(new URL("/", window.location.origin).toString());
  }

  function consumeJustUpdated(): boolean {
    ensureInit();
    if (!justUpdated) return false;
    justUpdated = false;
    return true;
  }

  function startUpdatedToast(durationMs: number) {
    updatedToastUntil.value = Date.now() + durationMs;
  }

  function hideUpdatedToast() {
    updatedToastUntil.value = null;
  }

  return {
    pendingReload,
    updatedToastUntil,
    updatedMessage,
    reloadNow,
    requestReload,
    consumeJustUpdated,
    startUpdatedToast,
    hideUpdatedToast,
  };
}

function isLocalSource(source: ReloadSource): source is LocalReloadSource {
  return source !== "app_control" && source !== "store";
}

const autoReloadDoneKey = (key: string) => `${AUTO_RELOAD_DONE_KEY_PREFIX}${key}`;

function safeSessionGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSessionSet(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {}
}

interface JustUpdatedFlag {
  at: number;
  message: string | null;
}

/**
 * อ่านธง "เพิ่งอัปเดต" แล้วลบทิ้งทันที — null = ไม่มีธง / หมดอายุ
 * ต้องลบทุกกรณีแม้ธงจะหมดอายุไปแล้ว ไม่งั้นค่าเก่าจะค้างใน localStorage ตลอดไป
 *
 * รับได้ทั้ง JSON { at, message } และตัวเลขล้วน (ธงที่ bundle ก่อนหน้าเขียนไว้)
 */
function consumeJustUpdatedFlag(channel: BuildChannel): JustUpdatedFlag | null {
  const raw = safeGet(justUpdatedKey(channel));
  if (raw === null) return null;

  safeRemove(justUpdatedKey(channel));

  let flag: JustUpdatedFlag;
  try {
    const parsed = JSON.parse(raw);
    flag =
      typeof parsed === "number"
        ? { at: parsed, message: null }
        : { at: Number(parsed?.at), message: text(parsed?.message) };
  } catch {
    return null;
  }

  if (!Number.isFinite(flag.at)) return null;
  return Date.now() - flag.at < JUST_UPDATED_TTL_MS ? flag : null;
}

/**
 * hash ต้องเป็น string ที่ไม่ว่าง (เผื่อคนพิมพ์ในคอนโซลเป็น number ก็รับ)
 * ค่าว่าง / null / object ให้ถือว่า "ไม่มีสัญญาณ" ปลอดภัยกว่าเดา
 */
function normalizeHash(value: unknown): string | null {
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (typeof value !== "string") return null;

  const hash = value.trim();
  return hash ? hash : null;
}

function text(value: unknown): string | null {
  if (typeof value !== "string") return null;
  return value.trim() || null;
}

/**
 * ส่งขึ้น Sentry ว่ารอบนี้สั่งโหลดใหม่เพราะอะไร
 * ยิงตอน "ตัดสินใจ" ไม่ใช่ตอนโหลดจริง เพราะเครื่องที่ติดงานค้างจะรอไปอีกนาน
 * แต่เราอยากรู้ตั้งแต่ตอนที่สัญญาณมาถึงว่ามันไปถึงกี่เครื่อง
 */
function reportToSentry(channel: BuildChannel, pending: PendingReload, storeId: string) {
  try {
    Sentry.captureMessage("[appControl] force reload", {
      level: "info",
      extra: {
        channel,
        hash: pending.hash,
        reason: pending.reason,
        storeId,
        source: pending.source,
      },
    });
  } catch {
    // Sentry ไม่ได้ init (dev / localhost) — ไม่ใช่เหตุให้ล้มการโหลดใหม่
  }
}

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // private mode / quota เต็ม — จดไม่ได้ก็ต้องยอม แต่ห้ามให้ throw ไปล้มการทำงานอื่น
  }
}

function safeRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {}
}

// ต้องลงทะเบียนก่อนใครสร้างสโตร์ appControl (appControl.client.ts) ไม่งั้น plugin ไม่ติดสโตร์
// ลำดับคุมใน modules/core/index.ts (addPlugin ตัวที่ประกาศทีหลังรันก่อน)
// ห้ามใส่ enforce: "pre" — plugin ของ @pinia/nuxt ไม่ใช่ pre ตอนนั้นยังไม่มี $pinia
export default defineNuxtPlugin({
  name: "app-reload",
  setup({ $pinia }) {
    ($pinia as any).use(AppReloadPlugin);
  },
});
